// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { GanttAxis } from '@/billing/lifecycle/gantt-math';
import {
  GANTT_BAR_HEIGHT_PX,
  GANTT_BAR_VERTICAL_PADDING_PX,
  GANTT_ROW_HEIGHT_PX,
  MIN_BAR_WIDTH_PX,
  buildAxis,
  daysToPercent,
  shouldShowBarLabel,
} from '@/billing/lifecycle/gantt-math';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import type { CycleTimeMetric } from '@/report/api/types';
import type { CycleTimeSegment } from '@/report/components/cycle-time-metrics';
import {
  formatCycleTimeAxisDays,
  formatCycleTimeDays,
} from '@/report/components/cycle-time-metrics';
import { pluralize } from '@/utilities/pluralize';
import { Box, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import { useRef } from 'react';

interface CycleTimeGanttProps<K extends string> {
  report: Record<K, CycleTimeMetric> | null;
  segments: readonly CycleTimeSegment<K>[];
  singularNoun: string;
  isLoading?: boolean;
}

interface CycleTimeGanttRow {
  key: string;
  label: string;
  tooltip: string;
  color: string;
  count: number;
  startDay: number;
  endDay: number;
  durationDays: number;
}

function buildCycleTimeAxis(rows: CycleTimeGanttRow[]): {
  axis: GanttAxis;
  leftMostDay: number;
  rightMostDay: number;
} {
  const dayValues = rows.flatMap((row) => [row.startDay, row.endDay]);
  const leftMostDay = Math.min(...dayValues);
  const rightMostDay = Math.max(...dayValues);

  return {
    axis: buildAxis(leftMostDay, rightMostDay),
    leftMostDay,
    rightMostDay,
  };
}

function buildRows<K extends string>(
  report: Record<K, CycleTimeMetric>,
  segments: readonly CycleTimeSegment<K>[]
): CycleTimeGanttRow[] {
  let cursor: number | null = 0;
  return segments.flatMap((segment) => {
    const durationDays = report[segment.key].medianDays;
    if (cursor === null || durationDays === null) {
      cursor = null;
      return [];
    }

    const startDay = cursor;
    const endDay = startDay + durationDays;
    cursor = endDay;

    return [
      {
        key: segment.key,
        label: segment.label,
        tooltip: segment.tooltip,
        color: segment.color,
        count: report[segment.key].count,
        startDay,
        endDay,
        durationDays,
      },
    ];
  });
}

export function CycleTimeGantt<K extends string>(
  props: CycleTimeGanttProps<K>
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidthPx } = useResizeObserver(containerRef, {
    debounceMs: 0,
    initialDelayMs: 0,
  });

  if (props.isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Skeleton
          data-testid="cycle-time-gantt-skeleton"
          variant="rectangular"
          height={128}
        />
      </Paper>
    );
  }

  if (props.report === null) {
    return null;
  }

  const rows = buildRows(props.report, props.segments);
  if (rows.length === 0) {
    return null;
  }

  const { axis, leftMostDay, rightMostDay } = buildCycleTimeAxis(rows);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box ref={containerRef} sx={{ position: 'relative', overflow: 'hidden' }}>
        {rows.map((row) => (
          <GanttRow
            key={row.key}
            row={row}
            axis={axis}
            axisWidthPx={containerWidthPx}
            singularNoun={props.singularNoun}
          />
        ))}
        {rows.slice(0, -1).map((row, index) => {
          const next = rows[index + 1];
          if (next === undefined) {
            return null;
          }

          const currentChainIndex = props.segments.findIndex(
            (segment) => segment.key === row.key
          );
          const nextChainIndex = props.segments.findIndex(
            (segment) => segment.key === next.key
          );
          if (nextChainIndex !== currentChainIndex + 1) {
            return null;
          }

          const leftPct = daysToPercent(row.endDay, axis);
          const topPx =
            index * GANTT_ROW_HEIGHT_PX +
            GANTT_BAR_VERTICAL_PADDING_PX +
            GANTT_BAR_HEIGHT_PX;
          const heightPx = 2 * GANTT_BAR_VERTICAL_PADDING_PX;
          return (
            <Box
              key={`connector-${row.key}`}
              data-testid="cycle-time-gantt-connector"
              sx={{
                position: 'absolute',
                left: `${leftPct}%`,
                top: `${topPx}px`,
                width: '1px',
                height: `${heightPx}px`,
                transform: 'translateX(-50%)',
                backgroundImage: (theme) =>
                  `linear-gradient(to bottom, ${theme.palette.grey[400]} 50%, transparent 50%)`,
                backgroundSize: '1px 3px',
                backgroundRepeat: 'repeat-y',
                pointerEvents: 'none',
              }}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {formatCycleTimeAxisDays(leftMostDay)}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'text.primary' }} />
        <Typography variant="caption" color="text.secondary">
          {formatCycleTimeAxisDays(rightMostDay)}
        </Typography>
      </Box>
    </Paper>
  );
}

function GanttRow(props: {
  row: CycleTimeGanttRow;
  axis: GanttAxis;
  axisWidthPx: number;
  singularNoun: string;
}) {
  const leftPct = daysToPercent(
    Math.min(props.row.startDay, props.row.endDay),
    props.axis
  );
  const widthPct =
    daysToPercent(Math.max(props.row.startDay, props.row.endDay), props.axis) -
    leftPct;
  const displayValue = formatCycleTimeDays(props.row.durationDays);
  const showLabel = shouldShowBarLabel(
    widthPct,
    props.axisWidthPx,
    displayValue
  );

  return (
    <Box
      data-testid="cycle-time-gantt-row"
      sx={{
        position: 'relative',
        height: GANTT_ROW_HEIGHT_PX,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          height: GANTT_BAR_HEIGHT_PX,
          bgcolor: 'action.hover',
        }}
      >
        <Tooltip
          arrow
          title={
            <GanttTooltipContent
              row={props.row}
              displayValue={displayValue}
              singularNoun={props.singularNoun}
            />
          }
        >
          <Box
            data-testid="cycle-time-gantt-bar"
            data-left-pct={leftPct}
            data-width-pct={widthPct}
            data-color={props.row.color}
            style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              minWidth: MIN_BAR_WIDTH_PX,
              bgcolor: props.row.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {showLabel && (
              <Typography
                variant="caption"
                sx={{
                  color: 'common.white',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  px: 0.5,
                }}
              >
                {displayValue}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}

function GanttTooltipContent(props: {
  row: CycleTimeGanttRow;
  displayValue: string;
  singularNoun: string;
}) {
  return (
    <>
      {props.row.label}: {props.displayValue} (
      {pluralize(props.row.count, { singular: props.singularNoun })})
      <br />
      <br />
      {props.row.tooltip}
    </>
  );
}
